/*
 * Cardul unui articol de autoservire.
 *
 * Scos din caseta si pus aici fiindca îl desenează acum TREI ecrane: caseta de
 * pe formularul de tichet, catalogul, si lista bazei de cunoștințe. Trei copii
 * ar fi însemnat că fraza cu minutele apare într-una dintre ele altfel — sau
 * nu apare deloc — și nimeni n-ar fi observat până când cineva ar fi comparat
 * două ecrane deschise unul lângă altul.
 *
 * Dunga din stânga spune dintr-o privire că ăsta e de CITIT, nu de cerut.
 * Cardul de serviciu de lângă el are un buton „Adaugă în coș"; fără un semn
 * vizual, cele două se citesc ca fiind același lucru.
 */

import { Link, useNavigate } from 'react-router-dom';
import * as shop from '../api/shop';
import type { HelpArticleSummary } from '../api/types';
import { t } from '../lib/i18n';
import { solveTimeLabel } from '../lib/selfServe';

interface Props {
  article: HelpArticleSummary;
  /**
   * Căutarea din care a ieșit articolul, când vine dintr-una.
   *
   * Zero pe ecranele în care omul răsfoiește: acolo n-a căutat nimic, deci
   * n-are ce raporta. Raportat oricum, jurnalul ar fi arătat „a deschis un
   * articol" pentru căutări care n-au existat.
   */
  searchId?: number;
}

/**
 * Raportează ce a făcut omul, și NU lasă nimic să iasă din ea.
 *
 * E o măsură pentru noi, luată pe drum. Un om căruia i s-ar arăta „raportarea a
 * eșuat" în timp ce deschide un articol ar crede că articolul n-a mers.
 */
function report(searchId: number, articleId: number): void {
  if (searchId <= 0) return;

  void shop.searchOutcome(searchId, 'article', articleId).catch(() => {
    /* Intenționat gol. Vezi mai sus. */
  });
}

export function ArticleCard({ article, searchId = 0 }: Props) {
  const navigate = useNavigate();
  const minutes = solveTimeLabel(article.self_serve_minutes);

  return (
    <article className="card card--help">
      <p className="card__eyebrow micro-label">{article.category?.name ?? t('Help')}</p>
      <h3 className="card__title">
        <Link to={`/help/${article.id}`} state={{ searchId }} onClick={() => report(searchId, article.id)}>
          {article.title}
        </Link>
      </h3>
      {article.summary !== '' ? <p className="card__text">{article.summary}</p> : null}

      {/* Fraza se arată numai când cineva chiar a măsurat. Vezi lib/selfServe. */}
      {minutes !== '' ? <p className="card__time">{minutes}</p> : null}

      <div className="card__actions">
        <button
          type="button"
          className="btn btn--sm btn--primary"
          onClick={() => {
            report(searchId, article.id);
            /* Cautarea merge mai departe cu omul: daca articolul nu-l ajuta,
               cererea deschisa de acolo poarta tot ce a incercat. */
            navigate(`/help/${article.id}`, { state: { searchId } });
          }}
        >
          {t('Read the steps')}
        </button>
      </div>
    </article>
  );
}
